import { Button, IconButton, Text } from '@acast-tech/decibel';
import { Glyph, XmarkSolidGlyph } from '@acast-tech/decibel-glyphs';
import { useModalDownload } from '@/hooks/modals';
import type { DateRange, DownloadConfig, ShowFilter } from '@/types';

type ModalHeaderProps = {
  title: string;
  closeModal: () => void;
  modalDateRange: DateRange;
  modalSelectedShow: ShowFilter;
  downloadConfig?: DownloadConfig;
};

/**
 * This component is used to render the header of a modal.
 * @param title - The title of the modal.
 * @param closeModal - The function to close the modal.
 * @param modalDateRange - The date range of the modal.
 * @param modalSelectedShow - The selected show of the modal.
 * @param downloadConfig - The download config for the modal.
 * @returns The modal header component.
 */
const ModalHeader = ({
  title,
  closeModal,
  downloadConfig,
  modalDateRange,
  modalSelectedShow,
}: ModalHeaderProps) => {
  const { isDownloading, handleDownload } = useModalDownload();
  return (
    <div className="sticky top-0 flex gap-4 items-center justify-between p-4 border-b border-[#DEDEF2] bg-white z-max">
      <Text variant="h3" mb={0} ml={2} data-testid="modal-header-title">
        {title}
      </Text>
      {downloadConfig && (
        <div className="ml-auto" data-testid="header-children">
          {downloadConfig && (
            <Button
              variant="secondary"
              glyph={downloadConfig.downloadButtonIcon as Glyph}
              onClick={() =>
                handleDownload(() => downloadConfig.onDownload(modalDateRange, modalSelectedShow))
              }
              busy={isDownloading}
            >
              {downloadConfig.downloadButtonText || 'Download'}
            </Button>
          )}
        </div>
      )}
      <IconButton
        aria-label="close"
        variant="tertiary"
        glyph={XmarkSolidGlyph}
        onClick={closeModal}
      />
    </div>
  );
};

export default ModalHeader;
